import React, { useCallback, useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { GlobalBox } from 'components/Box/GlobalBox';
import { Autocomplete, Grid, TextField } from '@mui/material';
import Snackbars from "components/Snackbar/Snackbar.js";
import { AttachMoney } from '@mui/icons-material'
import RegularButton from 'components/CustomButtons/Button'
import { useSearch } from 'hooks/useSearch'
import { 
  getAllContracts,
  resetContractSelected,
  changeContractSelected,
} from 'redux/actions/contractsCatalog/contractsCatalogActions'
import { 
  createPrePayroll,
  getPrePayroll,
  clearPrePayroll
} from 'redux/actions/prePayroll/prePayrollActions';
import PrePayrollTabs from './PrePayrollTabs';
import Datetime from 'react-datetime'
import moment from 'moment';
import './inputStyle.css'
import { getContractPeriods } from './GetContractPeriods';

export const PrePayroll = () => {
  const { 
    changeStation, 
    setSnackbar, 
    clearSnackbar, 
    clearSearch
  } = useSearch();

  const { token } = useSelector(state => state.login)
  const { prePayrollsObj } = useSelector(state => state.prePayroll)
  const { contractsArr, contractSelected } = useSelector(state => state.contractsCatalog)
  const { stationsSelected, color, message, isOpen: isSnackbarOpen } = useSelector((state) => state.search);
  const { stationsList } = useSelector(state => state.companies);
  const [periods, setPeriods] = useState({})
  const [date, setDate] = useState(moment())
  const [week, setWeek] = useState(null)
  const dispatch = useDispatch()

  const handleChangeStations = (_e, item) => {
    if(item) changeStation(item)
    else changeStation([])
  }
  
  const handleChangeContracts = (_e, item) => {
    if(item) dispatch(changeContractSelected(item.id))
    else dispatch(resetContractSelected())
  }
  
  const handleSubmit = useCallback(async () => {
    if (stationsSelected.length < 1) {
      setSnackbar("No se ha seleccionado alguna planta.");
      return;
    }

    if(!contractSelected) {
      setSnackbar("No se ha seleccionado algun contrato.");
      return;
    }

    if(week?.obtainable) {
      dispatch(getPrePayroll(token, week.obtainable))
      return;
    }

    let startDate, endDate, period

    if(contractSelected?.periodCalc === 'SEMANAL') {
      if(!week) {
        setSnackbar("No se ha seleccionado alguna semana.");
        return;
      }
      startDate = moment(date).clone().week(week.value).weekday(1).format('YYYY-MM-DD')
      endDate = moment(date).clone().week(week.value).weekday(7).format('YYYY-MM-DD')
      period = `${moment(date).clone().format('YYYY')}_WEEK_${week.value}`
    } else if (contractSelected?.periodCalc === 'MENSUAL') {
      startDate = moment(date).clone().startOf('month').format('YYYY-MM-DD')
      endDate = moment(date).clone().endOf('month').format('YYYY-MM-DD')
      period = `${moment(date).clone().format('YYYY')}_MONTH_${moment(date).clone().format('MM')}`
    }
    dispatch(createPrePayroll(token, stationsSelected.id, contractSelected.id, startDate, endDate, period));
  }, [token, stationsSelected, contractSelected, week, date]);

  // Effects
  useEffect(() => {
    dispatch(clearPrePayroll());
  }, [stationsSelected]);

  useEffect(() => {
    if(contractSelected && !periods[contractSelected.id]) {
      (async () => setPeriods({
        ...periods, 
        [contractSelected.id]: await getContractPeriods(contractSelected.id, setSnackbar)
      }))()
    }
    // eslint-disable-next-line
  }, [contractSelected]);

  const options = useMemo(() => {
    if(!contractSelected || contractSelected?.periodCalc !== 'SEMANAL') return []
    
    const length = moment(date).clone().year() === moment().clone().year()
      ? moment().clone().week() - 1
      : moment(date).clone().weeksInYear()

    return Array.from({ length }, (_v, i) => {
      const obtainable = periods[contractSelected.id]?.find(({period}) => period == `${moment(date).clone().format('YYYY')}_WEEK_${i + 1}`)
      let label = 'Week ' + (i + 1) 
      if(obtainable) label = label + ' Calculada'
      return {
        value: i + 1, 
        label, 
        obtainable 
      }
    })
  }, [periods, contractSelected, date]);

  // Sync week when options change (e.g. period status updated)
  useEffect(() => {
    if(week?.value && options.length) {
      const match = options.find(opt => opt.value === week.value)
      if(match && match !== week) setWeek(match)
    }
    // eslint-disable-next-line
  }, [options]);

  useEffect(() => {
    dispatch(clearPrePayroll());
    dispatch(resetContractSelected())
    clearSearch();
  }, []);

  useEffect(() => {
    if(prePayrollsObj?.status?.period !== week?.obtainable?.period)
      dispatch(clearPrePayroll());
  }, [week]);

  useEffect(() => {
    dispatch(clearPrePayroll());
    setWeek(null)
  }, [stationsSelected, contractSelected, date]);

  useEffect(() => {
    if(contractsArr.length === 0) dispatch(getAllContracts(token));
    // eslint-disable-next-line
  }, [token])

  useEffect(() => {
    if(prePayrollsObj.status) {
      if(week?.obtainable) setWeek({...week, obtainable: prePayrollsObj.status})
      else setWeek({...week, label:  `${week?.label} Calculada`, obtainable: prePayrollsObj.status})
      if(periods[contractSelected?.id] && periods[contractSelected.id].find(savedPeriod => savedPeriod.period === prePayrollsObj.status.period
      )?.status !== prePayrollsObj.status.status || periods[contractSelected?.id] && !periods[contractSelected.id].length) {
        setPeriods({
          ...periods, 
          [contractSelected.id]: periods[contractSelected.id].length 
            ? [...periods[contractSelected.id], prePayrollsObj.status]
            : [prePayrollsObj.status]
        })
      }
    }
    // eslint-disable-next-line
  },[prePayrollsObj])

  return (
    <React.Fragment>
      <GlobalBox
        icon={<AttachMoney />}
        title="Pre Nomina"
      >
        <Grid container spacing={2} sx={{ display: 'flex', alignItems: 'center' }}>
          <Grid 
            item 
            xs={12} 
            sm={contractSelected?.periodCalc ? 3 : 4}
          >    
            <Autocomplete
              size="small"
              filterSelectedOptions
              options={stationsList}
              getOptionLabel={(station) => station.label}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  name="station"
                  value={stationsSelected?.id}
                  label="Buscar por plantas"
                  variant="outlined"
                />
              )}
              fullWidth
              onChange={(_e, v) => handleChangeStations(_e, v)}
              noOptionsText={"No hay resultados"}
            />
          </Grid>
          <Grid
            item 
            xs={12} 
            sm={contractSelected?.periodCalc ? 3 : 4}
          >
            <Autocomplete
              size="small"
              key={stationsSelected?.id}
              filterSelectedOptions
              options={contractsArr.filter(contract => contract.idStation === stationsSelected?.id)}
              getOptionLabel={(contract) => contract.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  name="contract"
                  value={contractSelected?.id}
                  label="Buscar por contrato"
                  variant="outlined"
                />
              )}
              fullWidth
              onChange={(_e, v) => handleChangeContracts(_e, v)}
              noOptionsText={"No hay resultados"}
            />
          </Grid>
          {
            contractSelected?.periodCalc && 
              <Grid
                item 
                xs={12} 
                sm={contractSelected?.periodCalc === 'SEMANAL' ? 2 : 3}
              > 
                <Datetime
                  className='.datetimeSearch'
                  style={{height: '100%'}}
                  name="date"
                  dateFormat={contractSelected?.periodCalc === 'SEMANAL' ? 'YYYY' : 'YYYY-MMMM'}
                  initialDate={new Date()}
                  value={date} 
                  onChange={(value) => setDate(value)}
                  isValidDate={(curr) => curr.isBefore(moment())}
                  timeFormat={false}
                />
              </Grid>
          }
          {
            contractSelected?.periodCalc === 'SEMANAL' &&
              <Grid
                item 
                xs={12} 
                sm={2}
              >
                <Autocomplete
                  size="small"
                  filterSelectedOptions
                  options={options}
                  getOptionLabel={(week) => week?.label}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      name="week"
                      label="Semana"
                      variant="outlined"
                    />
                  )}
                  fullWidth
                  value={week}
                  onChange={(_e, v) => setWeek(v)}
                  noOptionsText={"No hay resultados"}
                />
              </Grid>
          }
          <Grid item xs={12} sm={1}>
            <RegularButton onClick={handleSubmit} color='info' fullWidth>Buscar</RegularButton>
          </Grid>
        </Grid>
        {
          Object.keys(prePayrollsObj).length > 0
          && <PrePayrollTabs periods={periods} setPeriods={setPeriods}/>
        }
      </GlobalBox>

      <Snackbars
        place="tr"
        color={color}
        message={message}
        open={isSnackbarOpen}
        closeNotification={clearSnackbar}
        close
      /> 
    </React.Fragment>
  )
}
